import React from 'react';
import { User, Shield, Eye, Edit2, Trash2, Power, PowerOff, Unlock } from 'lucide-react';
import { PermissionLine } from '../roles/PermissionPicker';

const SystemUsersList = ({ users, catalog, onView, onEdit, onDelete, onToggleStatus }) => {
    if (users.length === 0) {
        return (
            <div className="text-center py-12 bg-white rounded-lg border border-gray-200">
                <User className="w-12 h-12 text-gray-400 mx-auto mb-4" />
                <p className="text-gray-600 mb-4">No system users found</p>
                <p className="text-sm text-gray-500">Try adjusting your filters or add a new user</p>
            </div>
        );
    }

    const getRoleBadgeColor = (role) => {
        switch (role) {
            case 'SUPERADMIN':
                return 'bg-purple-100 text-purple-800';
            case 'ADMIN':
                return 'bg-blue-100 text-blue-800';
            case 'MANAGER':
                return 'bg-indigo-100 text-indigo-800';
            case 'DATA_OFFICER':
                return 'bg-teal-100 text-teal-800';
            default:
                return 'bg-gray-100 text-gray-800';
        }
    };

    const getStatusBadgeColor = (status) => {
        switch (status) {
            case 'ACTIVE':
                return 'bg-green-100 text-green-800';
            case 'LOCKED':
                return 'bg-amber-100 text-amber-800';
            case 'SUSPENDED':
                return 'bg-red-100 text-red-800';
            case 'INACTIVE':
                return 'bg-gray-100 text-gray-800';
            default:
                return 'bg-gray-100 text-gray-800';
        }
    };

    const formatDate = (dateString) => {
        if (!dateString) return 'N/A';
        return new Date(dateString).toLocaleDateString('en-US', {
            year: 'numeric',
            month: 'short',
            day: 'numeric'
        });
    };

    return (
        <div className="bg-white rounded-xl shadow-sm border border-gray-200 overflow-hidden">
            <div className="overflow-x-auto">
                <table className="w-full">
                    <thead className="bg-gray-50 border-b border-gray-200">
                    <tr>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                            User
                        </th>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                            Role
                        </th>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                            Permissions
                        </th>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                            Last Login
                        </th>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                            Status
                        </th>
                        <th className="px-6 py-3 text-center text-xs font-medium text-gray-500 uppercase tracking-wider">
                            Actions
                        </th>
                    </tr>
                    </thead>
                    <tbody className="divide-y divide-gray-200 bg-white">
                    {users.map((user) => (
                        <tr key={user.id} className="hover:bg-gray-50 transition">
                            <td className="px-6 py-4 whitespace-nowrap">
                                <div className="flex items-center">
                                    <div className="flex-shrink-0 w-10 h-10 bg-gray-100 rounded-full flex items-center justify-center">
                                        <User className="w-5 h-5 text-gray-600" />
                                    </div>
                                    <div className="ml-4">
                                        <div className="text-sm font-medium text-gray-900">
                                            {user.firstName} {user.lastName}
                                        </div>
                                        <div className="text-sm text-gray-500">
                                            {user.email}
                                        </div>
                                    </div>
                                </div>
                            </td>
                            <td className="px-6 py-4 whitespace-nowrap">
                                    <span className={`px-2 py-1 text-xs rounded-full flex items-center gap-1 w-fit ${getRoleBadgeColor(user.roleName)}`}>
                                        <Shield className="w-3 h-3" />
                                        {user.roleLabel || user.roleName}
                                    </span>
                            </td>
                            <td className="px-6 py-4">
                                {catalog && (
                                    <PermissionLine
                                        catalog={catalog}
                                        permissions={user.permissions}
                                        everything={user.roleName === 'SUPERADMIN'}
                                        own={user.ownPermissions}
                                    />
                                )}
                            </td>
                            <td className="px-6 py-4 whitespace-nowrap">
                                <div className="text-sm text-gray-600">
                                    {formatDate(user.lastLogin)}
                                </div>
                            </td>
                            <td className="px-6 py-4 whitespace-nowrap">
                                    <span className={`px-2 py-1 text-xs rounded-full ${getStatusBadgeColor(user.status)}`}>
                                        {user.status}
                                    </span>
                            </td>
                            <td className="px-6 py-4 whitespace-nowrap text-center">
                                <div className="flex items-center justify-center gap-2">
                                    <button
                                        onClick={() => onView(user)}
                                        className="text-gray-600 hover:text-blue-600 transition"
                                        title="View Details"
                                    >
                                        <Eye className="w-4 h-4" />
                                    </button>
                                    <button
                                        onClick={() => onEdit(user)}
                                        className="text-gray-600 hover:text-indigo-600 transition"
                                        title="Edit User"
                                    >
                                        <Edit2 className="w-4 h-4" />
                                    </button>
                                    <button
                                        onClick={() => onToggleStatus(user)}
                                        className={`transition ${
                                            user.status === 'ACTIVE'
                                                ? 'text-gray-600 hover:text-orange-600'
                                                : 'text-gray-600 hover:text-green-600'
                                        }`}
                                        title={
                                            user.status === 'ACTIVE' ? 'Suspend User'
                                                : user.status === 'LOCKED' ? 'Unlock User'
                                                    : 'Activate User'
                                        }
                                    >
                                        {user.status === 'ACTIVE' ? (
                                            <PowerOff className="w-4 h-4" />
                                        ) : user.status === 'LOCKED' ? (
                                            <Unlock className="w-4 h-4" />
                                        ) : (
                                            <Power className="w-4 h-4" />
                                        )}
                                    </button>
                                    <button
                                        onClick={() => onDelete(user)}
                                        className="text-gray-600 hover:text-red-600 transition"
                                        title="Delete User"
                                    >
                                        <Trash2 className="w-4 h-4" />
                                    </button>
                                </div>
                            </td>
                        </tr>
                    ))}
                    </tbody>
                </table>
            </div>
        </div>
    );
};

export default SystemUsersList;
